import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { theme } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import { IGaps, IGapsData } from 'store/ducks/Gaps/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { gapNoticedOptions } from 'constants/filterOptions';
import { getDynamicCategories } from 'constants/categorieNames';
import i18n from 'constants/translation/i18n';
import CustomTable from '../CustomTable';
import useClickOutside from 'utils/hooks/useClickOutside';
import CustomSelect from 'components/CustomSelect';
import TextBoxesContainer from 'components/TextBoxesContainer';
import Helpers from 'utils/Helpers';
import TextAreaBoxGap from 'components/TextAreaBoxGap';
import { stripHtmlTags } from 'utils/stripHtmlTags';
import * as Styled from './GapsDescriptionTable.styled';
import FilterDropdown from 'components/FilterDropdown';

const { useToken } = theme;

interface IGapTableRow extends IGapsData {
  key: string;
}

const GapsDescriptionTable: React.FC = () => {
  const { token } = useToken();
  const dispatch = useDispatch();
  const { gapsSaveData, gapsUpdated } = useSelector(
    (state: RootState) => state.gaps
  );

  const {
    lang,
    loading,
    saveClicked,
    drawTable,
    gapsDescription,
    tableFilters,
    scheme,
    orgContext
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    loading: state.gaps.loading,
    gapsDescription: state.gaps.gapsDescription,
    tableFilters: state.gaps.tableFilters,
    saveClicked: state.complianceEst.saveClicked,
    scheme: state.global.scheme,
    orgContext: state.global.hierarchicalLevelsScope
  }));

  const [rows, setRows] = useState<IGapTableRow[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const categoryFilterRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useState({});
  const filterParams = Helpers.toApiFilters(orgContext);
  const [searchText, setSearchText] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    i18n.changeLanguage(lang);
    forceUpdate({});
  }, [lang]);

  const handleChildChange = (updatedData: IGaps) => {
    const updatedState = () => {
      const newState = { ...gapsSaveData };

      Object.keys(updatedData).forEach((key: number | string) => {
        if (newState.hasOwnProperty(key)) {
          newState[key] = {
            ...newState[key],
            ...updatedData[key]
          };
        } else {
          newState[key] = updatedData[key];
        }
      });

      return newState;
    };

    dispatch(gapsOp.setGapsSaveData(updatedState()));
  };

  const handleChildDelete = (key: number | string) => {
    const updatedState = { ...gapsSaveData };
    delete updatedState[key];
    dispatch(gapsOp.setGapsSaveData(updatedState));
  };

  useEffect(() => {
    if (!Helpers.isObjEmpty(gapsSaveData) && saveClicked) {
      const userInfo = Helpers.getUserInfo()?.user;

      const gapsWithOwner = Object.values(gapsSaveData).map((gap: any) => ({
        ...gap,

        gap_id: gap.gap_id && gap.gap_id > 0 ? gap.gap_id : generateUniqueId(),

        gap_owner_ref: userInfo?.email || userInfo?.id
      }));

      gapsOp
        .saveGaps(gapsWithOwner, lang)
        .then(() => {
          dispatch(complianceEstOp.setSaveClicked(false));
          dispatch(gapsOp.setGapsSaveData({}));
        })
        .catch(() => {
          dispatch(complianceEstOp.setSaveClicked(false));
        });
    }
  }, [saveClicked, gapsSaveData, lang, dispatch]);

  useEffect(() => {
    const isEmpty = Helpers.isObjEmpty(gapsSaveData);
    // Update save button state immediately
    dispatch(complianceEstOp.setSaveDisabled(isEmpty));
  }, [gapsSaveData, dispatch]);

  useClickOutside(
    categoryFilterRef,
    showCategoryFilter,
    () => setShowCategoryFilter(false),
    'category-filter-dropdown'
  );

  const renderCategoryDropdown = () => {
    if (!showCategoryFilter) return null;
    return (
      <Styled.CategoryDropdownPortal className='category-filter-dropdown'>
        {[{ value: 'all', label: 'All Categories' }, ...categoryOptions].map(
          (option) => (
            <Styled.DropdownOption
              key={option.value}
              selected={categoryFilter === option.label}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCategoryFilter(
                  option.value === 'all' ? 'all' : option.label
                );
                setShowCategoryFilter(false);
              }}
            >
              {option.label}
            </Styled.DropdownOption>
          )
        )}
      </Styled.CategoryDropdownPortal>
    );
  };

  const gapDefinitionColumns = [
    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <span>{i18n.t('CLN_REQ')}</span>

          <Styled.SearchContainer>
            <Styled.SearchIcon onClick={() => setShowSearch(!showSearch)} />

            {showSearch && (
              <Styled.SearchInput
                placeholder={i18n.t('SEARCH')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
            )}
          </Styled.SearchContainer>
        </Styled.ColumnHeaderContainer>
      ),
      render: (_: any, row: IGapTableRow) => (
        <Styled.ClientReqTextArea
          value={stripHtmlTags(row.value)}
          rows={3}
          level={row.level}
          readOnly={true}
          disabled={true}
          scheme={scheme}
          token={token}
        />
      )
    },
    {
      title: () => (
        <Styled.CategoryHeaderContainer
          ref={categoryFilterRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowCategoryFilter(!showCategoryFilter);
          }}
        >
          <span>{i18n.t('CATEGORY')}</span>
          <Styled.CategoryFilterIcon scheme={scheme} />
          {renderCategoryDropdown()}
        </Styled.CategoryHeaderContainer>
      ),
      render: (_: any, row: IGapTableRow) => (
        <CustomSelect options={categoryOptions} value={row.category} disabled />
      )
    },
    // {
    //   title: i18n.t('DROPPED_EVIDENCES'),
    //   width: 450,
    //   render: () => <Styled.PlaceholderDiv width={450} height={20} />
    // },
    {
      title: i18n.t('DROPPED_AUDIT_FINDINGS'),
      render: (_: any, row: IGapTableRow) => (
        <TextBoxesContainer data={row.AUDITS} descName='desc' />
      )
    },
    {
      title: () => (
        <Styled.MergedColumnsContainer>
          <Styled.MergedColumnSm token={token}>
            {i18n.t('GAP_NOTICED')}
          </Styled.MergedColumnSm>
          <Styled.MergedColumnMd token={token}>
            {i18n.t('GAPS')}
          </Styled.MergedColumnMd>
        </Styled.MergedColumnsContainer>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm
      }: FilterDropdownProps) => (
        <FilterDropdown
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          options={gapNoticedOptions}
          defaultValue='all'
          filterName='gapNoticed'
        />
      ),
      render: (_: any, row: IGapTableRow) => (
        <TextAreaBoxGap
          data={row.GAPS}
          refId={row.id}
          withCheckbox
          onChange={handleChildChange}
          onDelete={handleChildDelete}
          placeholder={'Gap description'}
        />
      )
    }
  ];

  const buildRows = (data: IGapsData[]): IGapTableRow[] =>
    data.map((item) => ({
      ...item,
      key: String(item.id)
    }));

  useEffect(() => {
    try {
      if (!gapsDescription || !Array.isArray(gapsDescription)) {
        return;
      }

      let dataToFilter = gapsDescription;

      if (searchText.trim()) {
        dataToFilter = gapsDescription.filter((item: IGapsData) =>
          item.value.toLowerCase().includes(searchText.toLowerCase())
        );
      }

      if (dataToFilter.length > 0) {
        // Handle the new simplified data structure
        if (
          Array.isArray(dataToFilter) &&
          dataToFilter[0] &&
          'id' in dataToFilter[0]
        ) {
          let filteredData = dataToFilter as unknown as IGapsData[];

          // Apply category filter
          if (categoryFilter !== 'all') {
            filteredData = filteredData.filter(
              (item: IGapsData) => item.category === categoryFilter
            );
          }

          // Apply gap noticed filter - filter based on whether gaps exist
          const gapNoticedValue = tableFilters
            ?.gapNoticed?.[0] as unknown as string;

          if (gapNoticedValue && gapNoticedValue !== 'all') {
            filteredData = filteredData.filter((item: IGapsData) => {
              const gapsArray = Object.values(item.GAPS || {});
              const hasGaps = gapsArray.length > 0;

              // If 'noticed' - show rows WITH gaps
              // If 'notNoticed' - show rows WITHOUT gaps
              return gapNoticedValue === 'noticed' ? hasGaps : !hasGaps;
            });
          }

          const builtRows = buildRows(filteredData);
          setRows(builtRows);
        }
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error('Filter error:', error);
    }
  }, [gapsDescription, tableFilters, searchText, categoryFilter]);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getDynamicCategories(lang);
        setCategoryOptions(categories);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategoryOptions([]);
      }
    };
    loadCategories();
  }, []);

  // Fetch gaps description data when drawTable becomes true
  useEffect(() => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;
    if (drawTable) {
      gapsOp
        .fetchGapsDescription('en' as any, filterParams)
        .then((response) => {
          try {
            if (Array.isArray(response) && response.length > 0) {
              // Data is now directly usable without additional wrapping
              dispatch(gapsOp.setGapsDescription(response));
            } else {
              dispatch(gapsOp.setGapsDescription([]));
            }
          } catch (error) {
            dispatch(gapsOp.setGapsDescription([]));
          }
        })
        .catch((error) => {
          dispatch(gapsOp.setGapsDescription([]));
        });
    }
  }, [dispatch, drawTable, orgContext]);

  // Add loading guard to prevent rendering with invalid data
  if (loading) {
    return (
      <CustomTable
        drawTable={false}
        columns={gapDefinitionColumns}
        itemsCount={0}
        dataSource={[]}
        token={token}
        scheme={scheme}
      />
    );
  }

  return (
    <>
      <style>{Styled.HiddenMainRowStyles}</style>
      {!gapsUpdated ? (
        <CustomTable
          drawTable
          token={token}
          scheme={scheme}
          columns={gapDefinitionColumns}
          dataSource={rows}
          itemsCount={rows.length}
          loading={loading}
        />
      ) : (
        <CustomTable
          drawTable={drawTable}
          columns={gapDefinitionColumns}
          itemsCount={0}
          dataSource={[]}
          token={token}
          scheme={scheme}
        />
      )}
    </>
  );
};

export default GapsDescriptionTable;
